import React from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.tsx'
import './index.css'

// Redirect the published *.lovable.app URL to the official domain.
// We skip this for the editor preview (id-preview--*.lovable.app) so building keeps working.
(() => {
  if (typeof window === 'undefined') return;
  const host = window.location.hostname;
  const isLovableHost = host.endsWith('.lovable.app');
  const isEditorPreview = host.startsWith('id-preview--');
  const isOfficial = host === 'solrdrinks.com' || host === 'www.solrdrinks.com';
  if (isLovableHost && !isEditorPreview && !isOfficial) {
    const target = 'https://solrdrinks.com' + window.location.pathname + window.location.search + window.location.hash;
    window.location.replace(target);
    // Stop further script execution while the browser navigates away.
    throw new Error('Redirecting to official domain');
  }
})();
import { getPerformanceMonitor } from "@/utils/performance";
import { initializeIOSFeatures } from "@/utils/ios";
import { monitoring, trackPageView } from './utils/monitoring';
import { getConfig, performHealthCheck, isFeatureEnabled } from './utils/environment';

// Initialize environment and monitoring
const config = getConfig();
console.log(`SOLR initialized in ${config.environment} mode`);

// Initialize performance monitoring
if (isFeatureEnabled('enablePerformanceMonitoring')) {
  const monitor = getPerformanceMonitor();
  
  // Log metrics after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      monitor.logMetrics();
      monitoring.trackPageLoad();
      monitoring.trackWebVitals();
    }, 2000);
  });
}

// Initialize iOS-specific features
initializeIOSFeatures();

// Track initial page view
if (isFeatureEnabled('enableAnalytics')) {
  trackPageView('Home');
}

// Perform health check
performHealthCheck().then(health => {
  console.log('Application health check:', health);
  if (health.status === 'unhealthy') {
    console.warn('Application is in unhealthy state:', health.checks);
  }
});

// Clear old service workers and browser caches so stale popup bundles cannot keep showing.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  }).catch(() => {});
}

try {
  localStorage.setItem('pwa-install-prompt-seen', 'true');
  localStorage.setItem('ios-install-prompt-seen', 'true');
  localStorage.setItem('pwa-install-dismissed', 'true');
  localStorage.setItem('exit_intent_permanently_disabled', 'true');
} catch {
  // Ignore storage cleanup failures.
}

if ('caches' in window) {
  caches.keys().then((names) => {
    names.forEach((name) => caches.delete(name));
  }).catch(() => {});
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);
